import {
  DataGrid,
  FilterToolbar,
  formatDate,
  StatusBadge,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminPlansParams,
  GetAdminPlansRes,
  useGetAdminPlans
} from '@ijoin/hooks';
import { Stack } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';

import { camelCase } from 'lodash';
import { FC, useState } from 'react';

import { LinkButton } from './LinkButton';
import { PlanActionsCell } from './PlanActionsCell';
import { usePlanOptions } from './usePlanOptions';

const PLAN_STATUS = {
  ARCHIVE: 'ea6ff457-3b8c-4c70-ba70-566b96178c87',
  DELETING: 'a1b2c3d4-e5f6-4a5b-8c7d-9e0f1a2b3c4d',
  LIVE: '5c1a4b9b-f33e-4038-bb86-b0710ae74887'
};

const isAdmin = () => localStorage.getItem('isAdmin') === '1';

const columns = (
  advisoryOrganizationId: string
): GridColDef<GetAdminPlansRes>[] => [
  {
    field: 'description',
    flex: 2,
    headerName: 'Description',
    renderCell: params => (
      <LinkButton
        data-component='link'
        data-testid={camelCase(params.row.description)}
        params={{ advisoryOrganizationId, planId: params.row.planId }}
        to='/organizations/$advisoryOrganizationId/plans/$planId'
        variant='text'>
        {params.value}
      </LinkButton>
    )
  },
  {
    field: 'externalPlanId',
    flex: 1,
    headerName: 'External Plan ID',
    sortable: false
  },
  {
    field: 'investmentVehicleType',
    flex: 1,
    headerName: 'Type',
    sortable: false
  },
  {
    field: 'status',
    flex: 0.75,
    headerName: 'Status',
    renderCell: params => (
      <StatusBadge
        severity={
          params.row.planStatusId === PLAN_STATUS.DELETING
            ? 'error'
            : params.value === 'ACTIVE'
              ? 'success'
              : 'warning'
        }>
        {params.row.planStatusId === PLAN_STATUS.DELETING
          ? 'Deleting'
          : params.value}
      </StatusBadge>
    ),
    sortable: false
  },
  {
    field: 'lastSync',
    flex: 1,
    headerName: 'Last Sync',
    sortable: false,
    valueFormatter: value =>
      value ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' }) : ''
  },
  {
    field: 'planId',
    flex: 1,
    headerName: 'Plan ID',
    sortable: false
  },
  ...(isAdmin()
    ? [
        {
          field: 'actions' as const,
          headerName: '',
          renderCell: (params: GridRenderCellParams<GetAdminPlansRes>) => (
            <PlanActionsCell
              advisoryOrganizationId={advisoryOrganizationId}
              row={params.row}
            />
          ),
          sortable: false,
          width: 67
        }
      ]
    : [])
];

export const PlansTab: FC<{ advisoryOrganizationId: string }> = props => {
  const [query, setQuery] = useState<GridQuery>();

  const planOptions = usePlanOptions(props.advisoryOrganizationId);

  const plans = useGetAdminPlans(
    props.advisoryOrganizationId,
    query as GetAdminPlansParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const filters: FilterDef[] = [
    {
      ...planOptions,
      label: 'Plan Name',
      name: 'planId',
      type: 'autocomplete'
    },
    { label: 'External Plan ID', name: 'externalPlanId', type: 'text' }
  ];

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns(props.advisoryOrganizationId)}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.planId}
        initialSort={[{ field: 'description', sort: 'asc' }]}
        loading={!query || plans.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{
          left: ['description'],
          right: isAdmin() ? ['actions'] : []
        }}
        rowCount={plans.data?.pagination?.total ?? 0}
        rows={plans.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'plans'
          },
          toolbar: {
            onRefresh: plans.refetch,
            searchButtonTestId: 'planSearchButton',
            triggerTestId: 'plansTab-FilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

PlansTab.displayName = 'PlansTab';
